import type { ContratApprentissageDTO } from '@/features/api/extranet/dto/Demande/CAPP'
import { type TypeContextCourant, TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { PropertyStatus, type PropertyStatusConfiguration } from '@/features/domaine/property-status'
import { getAge, isDate, isNullUndefinedOrEmptyString } from '@/features/shared/tools'
import { idccAutoriseTT } from '../shared'
import { isAdherentOuMandatGestion } from '../shared/tools'
import { typeContratAvenant } from './constantes'
import { getEstMajeurOuMineurEmancipe, getRemunerationProperty } from './tools'
import type { ContextReglesCapp } from './types'
import { withCache } from '@/features/shared/tools/function.ts'

const majeurOuMineurEmancipe = (
  _value: any,
  _parent: any,
  modele: ContratApprentissageDTO,
  context: ContextReglesCapp,
) => {
  const estMajeurOuMineurEmancipe = getEstMajeurOuMineurEmancipe({
    mineurEmancipe: modele.representantLegal?.mineurEmancipe,
    dateNaissance: modele.apprenti?.dateNaissance,
    dateConclusion: modele.contrat?.dateConclusion,
  })

  if (estMajeurOuMineurEmancipe) return PropertyStatus.NotPresent

  return isAdherentOuMandatGestion(context.typeContext, modele.estSousMandatGestion)
    ? PropertyStatus.Required
    : PropertyStatus.Optional
}

function selonTypeContextCourant(
  typeContext: TypeContextCourant,
  conditionVrai: PropertyStatus,
  conditionFaux: PropertyStatus,
) {
  return function (_value: any, _parent: any, modele: ContratApprentissageDTO, context: ContextReglesCapp) {
    const sousMandat = modele.estSousMandatGestion
    if (typeContext === TypesContextesAutorisesEnum.Adherent) {
      return context.typeContext === TypesContextesAutorisesEnum.Adherent || sousMandat ? conditionVrai : conditionFaux
    } else {
      return context.typeContext === TypesContextesAutorisesEnum.Prestataire || !sousMandat
        ? conditionVrai
        : conditionFaux
    }
  }
}

const optionnelPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.Optional,
)

const requiredOrNotPresentPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.NotPresent,
)

const optionnelOrNotPresentPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Optional,
  PropertyStatus.NotPresent,
)

const optionnelMaitreApprentissageSecondaire = (
  _value: any,
  _parent: any,
  modele: ContratApprentissageDTO,
  context: ContextReglesCapp,
) => {
  // console.log(idccAutoriseTT.includes(modele.employeur?.etablissement?.idcc?.code!))
  return (modele.tutorat?.maitreApprentissageSecondaire ||
    (idccAutoriseTT.includes(modele.employeur?.etablissement?.idcc?.code ?? '') &&
      modele.typePublic === 'Interimaire')) &&
    isAdherentOuMandatGestion(context.typeContext, modele.estSousMandatGestion)
    ? PropertyStatus.Required
    : PropertyStatus.Optional
}

function ventilationStatusBase(model: {
  index: number | undefined
  typeContext: TypeContextCourant
  estSousMandatGestion: boolean | null | undefined
  dateNaissance: string | null | undefined
  dateDebutExecutionContrat: string | null | undefined
  dateFinContratOuPeriodeApprentissage: string | null | undefined
}) {
  if (model.index === undefined || model.index < 0) return PropertyStatus.NotPresent
  if (!isAdherentOuMandatGestion(model.typeContext, model.estSousMandatGestion)) return PropertyStatus.NotPresent

  if (
    !isDate(model.dateNaissance) ||
    !isDate(model.dateDebutExecutionContrat) ||
    !isDate(model.dateFinContratOuPeriodeApprentissage)
  )
    return PropertyStatus.NotPresent

  const rem = getRemunerationProperty(
    new Date(model.dateNaissance),
    new Date(model.dateDebutExecutionContrat),
    new Date(model.dateFinContratOuPeriodeApprentissage),
  )

  return rem.at(model.index)?.status ?? PropertyStatus.NotPresent
}

const ventilationStatus = withCache(ventilationStatusBase, { cacheSize: 20 })

export const propertyStatusConfiguration: PropertyStatusConfiguration<ContratApprentissageDTO, ContextReglesCapp> = {
  intitule: PropertyStatus.Required,
  employeur: {
    secteurEntreprise: optionnelPrestataire,
    regimeSpecifiqueAssuranceChomage: (_value, parent, _model, context) => {
      if (parent.secteurEntreprise !== 'Public') return PropertyStatus.NotPresent
      return context.typeContext === TypesContextesAutorisesEnum.Adherent
        ? PropertyStatus.Required
        : PropertyStatus.Optional
    },
    effectifTotalEntreprise: requiredOrNotPresentPrestataire,
    employeurSpecifique: optionnelPrestataire,
    typeEmployeur: optionnelPrestataire,
    etablissement: {
      naf: {
        code: PropertyStatus.Required,
        intitule: PropertyStatus.Required,
      },
      idcc: {
        code: PropertyStatus.Required,
        intitule: PropertyStatus.Required,
      },
      typeEntreprise: PropertyStatus.Required,
    },
    contact: {
      nom: PropertyStatus.NotPresent,
      prenom: PropertyStatus.NotPresent,
      fonction: PropertyStatus.NotPresent,
      email: PropertyStatus.Required,
      telephone: PropertyStatus.Optional,
    },
  },
  apprenti: {
    prenom: PropertyStatus.Required,
    sexe: optionnelPrestataire,
    dateNaissance: PropertyStatus.Required,
    email: optionnelPrestataire,
    telephone: optionnelPrestataire,
    nomNaissance: PropertyStatus.Required,
    nirTemporaire: optionnelPrestataire,
    nir: optionnelPrestataire,
    nationalite: optionnelPrestataire,
    departementNaissance: optionnelPrestataire,
    communeNaissance: optionnelPrestataire,
    regimeSocial: optionnelPrestataire,
    csp: optionnelPrestataire,
    statutApprenti: optionnelPrestataire,
    situationAvantContrat: optionnelPrestataire,
    intituleDernierDiplomeOuTitrePrepare: optionnelPrestataire,
    dernierDiplomeOuTitrePrepare: optionnelPrestataire,
    derniereAnneeApprenti: optionnelPrestataire,
    diplomeOuTitreLePlusEleveObtenu: optionnelPrestataire,
    projetCreationOuReprise: optionnelPrestataire,
    inscritSurListeSportifs: optionnelPrestataire,
    beneficieReconnaissanceTravailleurHandicape: optionnelPrestataire,
    adresse: {
      //numero: PropertyStatus.Required,
      //type: PropertyStatus.Required,
      voie: optionnelPrestataire,
      codePostal: optionnelPrestataire,
      commune: optionnelPrestataire,
    },
    equivalenceJeune: (value, parent, globalModel, context) =>
      parent?.beneficieReconnaissanceTravailleurHandicape === false
        ? optionnelPrestataire(value, parent, globalModel, context)
        : PropertyStatus.NotPresent,
    extensionBOE: (value, parent, globalModel, context) =>
      parent?.beneficieReconnaissanceTravailleurHandicape === false
        ? optionnelPrestataire(value, parent, globalModel, context)
        : PropertyStatus.NotPresent,
  },
  formation: {
    referenceFormationInterneEntreprise: PropertyStatus.Optional,
    cfaEtablissement: {
      $model: PropertyStatus.Required,
    },
    estCfaEntreprise: PropertyStatus.Required,
    modalitePedagogique: PropertyStatus.Required,
    diplome: PropertyStatus.Required,
    typeQualification: PropertyStatus.Required,
    rncp: {
      code: PropertyStatus.Required
    },
    codeDiplome: {
      code: (_value, _parent, model) =>
        model.formation?.typeQualification === 'CodeDiplome' ? PropertyStatus.Required : PropertyStatus.Optional,
    },
    dateDebutFormationCfa: PropertyStatus.Required,
    datePrevueFinEpreuvesOuExamens: PropertyStatus.Required,
    dureeFormationEnHeures: PropertyStatus.Required,
    dontDureeFormationDistance: (_value, parent) =>
      parent?.modalitePedagogique === 'Mixte' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    lieuFormationIdentiqueCfaPrincipal: PropertyStatus.Required,

    cfaEtablissementLieuPrestation: {
      $model: (_model, parent) =>
        parent?.lieuFormationIdentiqueCfaPrincipal === false ? PropertyStatus.Required : PropertyStatus.NotPresent,
    },
    nsf: PropertyStatus.Required,
    formacode: PropertyStatus.Required,
    fraisSupportesCfa: {
      montant: (value, _index, parent, model, _context) => {
        if (
          parent.nature === 'MajorationTravailleurHandicape' &&
          model.apprenti?.beneficieReconnaissanceTravailleurHandicape !== true
        )
          return PropertyStatus.NotPresent

        return (!isNullUndefinedOrEmptyString(parent.quantite) || !isNullUndefinedOrEmptyString(value)) &&
          parent.baseCalcul === 'Quantite'
          ? PropertyStatus.Required
          : PropertyStatus.Optional
      },
      quantite: (value, _index, parent, _model, _context) =>
        (!isNullUndefinedOrEmptyString(parent.montant) || !isNullUndefinedOrEmptyString(value)) &&
        parent.baseCalcul === 'Quantite'
          ? PropertyStatus.Required
          : PropertyStatus.Optional,
    },
    fraisFactureCfa: {
      montant: PropertyStatus.Required,
    },
    /*

    diplome: PropertyStatus.Required,
    niveauDiplome: PropertyStatus.Required,
    */
  },
  contrat: {
    modeContractuel: optionnelPrestataire,
    typeContratAvenant: optionnelPrestataire,
    caisseRetraitreComplementaire: (_value, _parent, model, context) => {
      if (!isAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)) return PropertyStatus.NotPresent

      return model.employeur?.secteurEntreprise === '1' ? PropertyStatus.Required : PropertyStatus.Optional
    },
    numeroDecaPrecedent: (_value, _parent, model, context) =>
      isAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)
        ? PropertyStatus.Optional
        : PropertyStatus.NotPresent,
    dateConclusion: optionnelPrestataire,
    dateDebutExecutionContrat: optionnelPrestataire,
    dateDebutFormationChezEmployeur: optionnelPrestataire,
    dateEffetAvenant: (_value, parent) =>
      !!parent.typeContratAvenant && typeContratAvenant.includes(parent.typeContratAvenant)
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    dateFinContratOuPeriodeApprentissage: optionnelPrestataire,
    dureeHebdomadaire: {
      heure: optionnelPrestataire,
      minute: optionnelPrestataire,
    },
    travailMachinesDangereusesOuExpositionRisquesparticuliers: optionnelPrestataire,
    mobiliteInternationale: PropertyStatus.Required,
    lieu: (_value, parent) =>
      parent?.mobiliteInternationale === true ? PropertyStatus.Required : PropertyStatus.NotPresent,
    conditionRealisation: (_value, parent) =>
      parent?.mobiliteInternationale === true ? PropertyStatus.Required : PropertyStatus.NotPresent,
  },
  dispositif: PropertyStatus.Required,
  statut: PropertyStatus.Required,
  niveauPermission: PropertyStatus.Required,
  rowVersion: PropertyStatus.Required,
  consentementTraitementDonnees: PropertyStatus.Required,
  code: PropertyStatus.NotPresent,
  lieuSignature: optionnelPrestataire,
  attestationSurHonneur: PropertyStatus.Required,
  attestationPiecesJustificatives: optionnelPrestataire,
  validationNotice: PropertyStatus.Required,
  representantLegal: {
    mineurEmancipe: (value, parent, model, context) => {
      const estMajeur =
        isDate(model.apprenti?.dateNaissance) &&
        isDate(model.contrat?.dateConclusion) &&
        getAge(new Date(model.apprenti?.dateNaissance), new Date(model.contrat?.dateConclusion)) >= 18
      if (estMajeur) return PropertyStatus.NotPresent
      return optionnelPrestataire(value, parent, model, context)
    },
    estStructure: majeurOuMineurEmancipe,
    representant: {
      nom: majeurOuMineurEmancipe,
      prenom: (value, parent, model, context) => {
        // Le statut est optionnel quand le représentant légale est une structure
        const status = majeurOuMineurEmancipe(value, parent, model, context)
        if (model.representantLegal?.estStructure && status === PropertyStatus.Required) return PropertyStatus.Optional
        return status
      },
      fonction: majeurOuMineurEmancipe,
      email: majeurOuMineurEmancipe,
      telephone: PropertyStatus.Optional,
    },
    adresse: {
      voie: majeurOuMineurEmancipe,
      complement: PropertyStatus.Optional,
      codePostal: majeurOuMineurEmancipe,
      commune: majeurOuMineurEmancipe,
    },
  },
  tutorat: {
    maitreApprentissage: {
      prenom: optionnelPrestataire,
      sexe: optionnelPrestataire,
      dateNaissance: optionnelPrestataire,
      nomNaissance: optionnelPrestataire,
      email: optionnelPrestataire,
      emploiOccupe: optionnelPrestataire,
    },
    maitreApprentissageSecondaire: {
      prenom: optionnelMaitreApprentissageSecondaire,
      sexe: optionnelMaitreApprentissageSecondaire,
      dateNaissance: optionnelMaitreApprentissageSecondaire,
      nomNaissance: optionnelMaitreApprentissageSecondaire,
      email: optionnelMaitreApprentissageSecondaire,
      emploiOccupe: optionnelMaitreApprentissageSecondaire,
    },

    attestationTuteurCriteresEligibilite: optionnelPrestataire,
    attestationTuteurTroisApprentis: optionnelPrestataire,
    attestationTuteurFormationTuteur: PropertyStatus.Optional,
  },
  remuneration: {
    salaireBrutMensuelEmbauche: requiredOrNotPresentPrestataire,
    avantageAutre: optionnelOrNotPresentPrestataire,
    avantageLogement: optionnelOrNotPresentPrestataire,
    avantageNourriture: optionnelOrNotPresentPrestataire,
    ventilations: {
      pourcentage: (_value, index, _parent, model, context) => {
        return ventilationStatus({
          index,
          typeContext: context.typeContext,
          estSousMandatGestion: model.estSousMandatGestion,
          dateNaissance: model.apprenti?.dateNaissance,
          dateDebutExecutionContrat: model.contrat?.dateDebutExecutionContrat,
          dateFinContratOuPeriodeApprentissage: model.contrat?.dateFinContratOuPeriodeApprentissage,
        })
      },
      baseCalcul: (_value, index, _parent, model, context) => {
        return ventilationStatus({
          index,
          typeContext: context.typeContext,
          estSousMandatGestion: model.estSousMandatGestion,
          dateNaissance: model.apprenti?.dateNaissance,
          dateDebutExecutionContrat: model.contrat?.dateDebutExecutionContrat,
          dateFinContratOuPeriodeApprentissage: model.contrat?.dateFinContratOuPeriodeApprentissage,
        })
      },
      dateRange: {
        start: (_value, parent, model, context) => {
          const index = model.remuneration?.ventilations?.findIndex((item) => item.dateRange === parent) ?? -1
          return ventilationStatus({
            index,
            typeContext: context.typeContext,
            estSousMandatGestion: model.estSousMandatGestion,
            dateNaissance: model.apprenti?.dateNaissance,
            dateDebutExecutionContrat: model.contrat?.dateDebutExecutionContrat,
            dateFinContratOuPeriodeApprentissage: model.contrat?.dateFinContratOuPeriodeApprentissage,
          })
        },
        end: (_value, parent, model, context) => {
          const index = model.remuneration?.ventilations?.findIndex((item) => item.dateRange === parent) ?? -1
          return ventilationStatus({
            index,
            typeContext: context.typeContext,
            estSousMandatGestion: model.estSousMandatGestion,
            dateNaissance: model.apprenti?.dateNaissance,
            dateDebutExecutionContrat: model.contrat?.dateDebutExecutionContrat,
            dateFinContratOuPeriodeApprentissage: model.contrat?.dateFinContratOuPeriodeApprentissage,
          })
        },
      },
    },
  },
  fichiers: {
    $item: (value, _index, _parent, _model, context) => {
      if (context.naturesFichiers.obligatoires.includes(value?.nature ?? '')) return PropertyStatus.Required
      if (context.naturesFichiers.facultatifs.includes(value?.nature ?? '')) return PropertyStatus.Optional
      return PropertyStatus.NotPresent
    },
  },
} as const
